import type { RosterCandidate } from '../types'

/**
 * −1…+1 keyword-sentiment bucketing, shared between the why-line and the
 * dossier drilldown's sentiment dot/gauge (IncubationRosterPanel.tsx) so the
 * two never drift apart — this is the single copy, imported back into the
 * panel.
 */
export type Sentiment = 'neg' | 'neutral' | 'pos'

export function sentimentOf(meanKw: number): Sentiment {
  if (meanKw > 0.05) return 'pos'
  if (meanKw < -0.05) return 'neg'
  return 'neutral'
}

export const SENTIMENT_LABEL: Record<Sentiment, string> = {
  neg: 'negative',
  neutral: 'neutral',
  pos: 'positive',
}

/**
 * Revenue-YoY sign-crossing flag labels. Mirrors YOY_FLAG_META's label text
 * in IncubationRosterPanel.tsx so "rev loss → profit" in the why-line reads
 * identically to the pill shown when the row is expanded.
 */
const REVENUE_YOY_FLAG_LABEL: Record<string, string> = {
  loss_to_profit: 'loss → profit',
  profit_to_loss: 'profit → loss',
  loss_to_loss: 'loss → loss',
}

/** Whole-percent trend-excess magnitude, matching the ranking-scale rounding
 * used by the Trend column (fmtTrendPct in IncubationRosterPanel.tsx). */
function trendPart(trendExcess: number): string {
  const pct = Math.round(trendExcess * 100)
  const sign = pct >= 0 ? '+' : ''
  return `trend ${sign}${pct}%`
}

function revenuePart(cand: RosterCandidate): string | null {
  const fin = cand.dossier?.financials
  if (!fin) return null
  const { revenue_yoy, revenue_yoy_flag } = fin
  if (revenue_yoy !== null && revenue_yoy !== undefined && Number.isFinite(revenue_yoy)) {
    const pct = Math.round(revenue_yoy * 100)
    const sign = pct >= 0 ? '+' : ''
    return `rev ${sign}${pct}%`
  }
  if (revenue_yoy_flag) {
    const label = REVENUE_YOY_FLAG_LABEL[revenue_yoy_flag]
    if (label) return `rev ${label}`
  }
  return null
}

function newsPart(cand: RosterCandidate): string | null {
  const news = cand.dossier?.news
  if (!news || news.n_titles <= 0) return null
  return `news ${SENTIMENT_LABEL[sentimentOf(news.mean_kw)]} (${news.n_titles})`
}

/**
 * One-line "why this candidate" summary for the roster's symbol cell:
 * sector + trend-excess magnitude + revenue YoY (value or sign-crossing
 * flag label) + news sentiment, `·`-separated, skipping absent parts.
 * Never empty — trend-excess is always present, so it's the guaranteed
 * fallback when sector/dossier are unavailable.
 */
export function composeWhyLine(cand: RosterCandidate): string {
  const parts: string[] = []
  const sector = cand.sector?.trim()
  if (sector) parts.push(sector)
  parts.push(trendPart(cand.trend_excess))
  const rev = revenuePart(cand)
  if (rev) parts.push(rev)
  const news = newsPart(cand)
  if (news) parts.push(news)
  return parts.join(' · ')
}
